import { useState, useCallback } from "react";
import { apiFetch } from "../lib/api";

export interface Wine {
  id: string;
  type: string;
  name: string;
  mil: string;
  dom: string;
  prix: string;
  desc: string;
  gen: boolean;
  visible: boolean;
  highlighted: boolean;
}

export interface WineInput {
  type: string;
  name: string;
  mil: string;
  dom: string;
  prix: string;
}

// Defensive defaults so older payloads keep working until the backend reloads.
function withDefaults(w: any): Wine {
  return {
    ...w,
    visible: typeof w.visible === "boolean" ? w.visible : true,
    highlighted: typeof w.highlighted === "boolean" ? w.highlighted : false,
  };
}

export function useWines() {
  const [wines, setWinesState] = useState<Wine[]>([]);
  const [loading, setLoading] = useState(false);

  const setWines: React.Dispatch<React.SetStateAction<Wine[]>> = useCallback((value) => {
    setWinesState((prev) => {
      const next = typeof value === "function" ? (value as (p: Wine[]) => Wine[])(prev) : value;
      return next.map(withDefaults);
    });
  }, []);

  const fetchWines = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/wines`);
      const data = await res.json();
      setWinesState((Array.isArray(data) ? data : []).map(withDefaults));
    } finally {
      setLoading(false);
    }
  }, []);

  const addWine = useCallback(async (input: WineInput) => {
    const res = await apiFetch(`/wines`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    if (!res.ok) throw new Error("Erreur lors de l'ajout.");
    await fetchWines();
  }, [fetchWines]);

  const updateWine = useCallback(async (id: string, partial: Partial<Wine>): Promise<Wine> => {
    const res = await apiFetch(`/wines/${id}`, {
      method: "PATCH",
      body: JSON.stringify(partial),
    });
    if (!res.ok) throw new Error("Erreur lors de la mise à jour.");
    const updated = withDefaults(await res.json());
    setWinesState((ws) => ws.map((w) => (w.id === id ? updated : w)));
    return updated;
  }, []);

  const deleteWine = useCallback(async (id: string) => {
    await apiFetch(`/wines/${id}`, { method: "DELETE" });
    setWinesState((ws) => ws.filter((w) => w.id !== id));
  }, []);

  const addWinesBulk = useCallback(async (inputs: WineInput[]): Promise<Wine[]> => {
    const res = await apiFetch(`/wines/bulk`, {
      method: "POST",
      body: JSON.stringify(inputs),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Erreur" }));
      throw new Error(err.error || "Erreur lors de l'import.");
    }
    const created = (await res.json()).map(withDefaults);
    setWinesState((ws) => [...ws, ...created]);
    return created;
  }, []);

  return { wines, setWines, loading, fetchWines, addWine, updateWine, deleteWine, addWinesBulk };
}
